import { Injectable, NgZone, computed, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Platform } from '@ionic/angular';
import { Logger, LoggingService } from 'ionic-logging-service';
import { PlatformService } from '@app/services/platform/platform.service';
import { AuthService } from '@app/services/auth/auth.service';
import { IShareItem } from '@app/services/share-extension/models/share-item.model';

declare const cordova: any;

const GET_PENDING_FILE_PATH_TIMEOUT_MS = 5_000;
const READ_FILE_TIMEOUT_MS = 10_000;
const DELETE_FILE_TIMEOUT_MS = 5_000;

// Storage names written by the Share Extension are "<UUID>_<display name>"; parsed by fixed offset because a display name may itself contain '_'.
const STORAGE_PREFIX_LENGTH = 37;
const STORAGE_PREFIX_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i;

// Thrown by doStartReading when a newer share supersedes this one before the read begins — a normal outcome, not a failure.
export class ShareSupersededError extends Error {}

// Single source of truth for the share-extension lifecycle; all native-bridge ops go through the `queue` below so exactly one runs at a time.
type ShareState =
  | { phase: 'idle' }
  | { phase: 'detecting' }                                   // first-time scan from idle (getPendingFilePath); a re-scan while ready skips this phase
  | { phase: 'ready'; filePath: string; item: IShareItem }   // file detected, waiting for the page to read it
  | { phase: 'reading'; filePath: string; item: IShareItem }; // reading native (readSharedFile); back to idle once the bytes are handed over and the file is deleted

@Injectable({ providedIn: 'root' })
export class ShareExtensionService {

  // ############### STATE ###############

  private state = signal<ShareState>({ phase: 'idle' });

  // The share waiting to be consumed by DocumentPrintPage; null unless one is ready to read.
  public pendingShare = computed<IShareItem | null>(() => {
    const s = this.state();
    return s.phase === 'ready' ? s.item : null;
  });

  // True while a confirmed share is still owned here (ready/reading) — used for post-auth navigation and loading indicators.
  public pendingShareIntent = computed(() => {
    const phase = this.state().phase;
    return phase === 'ready' || phase === 'reading';
  });

  // Serializes every native-bridge operation so a trigger arriving mid-operation always sees the freshest state once its turn comes up.
  private queue: Promise<void> = Promise.resolve();

  private enqueue<T>(op: () => Promise<T>): Promise<T> {
    const methodName = 'enqueue';
    const result = this.queue.then(op);
    this.queue = result.then(() => undefined, (err) => {
      if (err instanceof ShareSupersededError) {
        this.logger.info(methodName + ' ShareExtension: queued operation superseded — ' + JSON.stringify({ error: String(err) }));
      } else {
        this.logger.error(methodName + ' ShareExtension: queued operation FAILED — ' + JSON.stringify({ error: String(err) }));
      }
    });
    return result;
  }

  // ############### INIT ###############

  private logger: Logger;

  constructor(
    private authService: AuthService,
    private loggingService: LoggingService,
    private ngZone: NgZone,
    private platform: Platform,
    private platformService: PlatformService,
    private router: Router
  ) {
    this.logger = this.loggingService.getLogger('[ShareExtensionService]');
    const methodName = 'ctor';
    this.logger.entry(methodName);

    // Cold-start scan on platform.ready; warm-start re-scan on every resume (Share Extension can wake an already-running app).
    this.platform.ready().then(() => {
      if (this.platformService.isCordova() && this.platformService.isIOS()) {
        this.enqueue(() => this.detectAndAdopt('platform.ready'));

        this.platform.resume.subscribe(() => {
          this.enqueue(() => this.detectAndAdopt('resume'));
        });
      }
    });
  }

  // ############### PUBLIC API ###############

  // Called by AppComponent when a printixapp://share URL wakes the app; the URL itself is just a signal, the file is read from the App Group container.
  public handleShareURL(url: string): void {
    const methodName = 'handleShareURL';
    this.logger.info(methodName + ': ShareExtension received share URL');
    if (!this.platformService.isCordova() || !this.platformService.isIOS()) { return; }
    this.enqueue(() => this.detectAndAdopt('handleShareURL'));
  }

  // Reads the pending file ('ready' -> 'reading' -> 'idle') and deletes it; rejects on supersede or native failure/timeout.
  public startReading(id: string): Promise<File> {
    return this.enqueue(() => this.doStartReading(id));
  }

  private async doStartReading(id: string): Promise<File> {
    const methodName = 'doStartReading';
    this.logger.info(methodName + ' ' + JSON.stringify({ id }));
    const current = this.state();
    if (current.phase !== 'ready' || current.item.id !== id) {
      throw new ShareSupersededError('ShareExtension: startReading — state superseded before read began');
    }

    const item = current.item;
    this.state.set({ phase: 'reading', filePath: current.filePath, item });

    try {
      return await this.nativeReadFile(item);
    } finally {
      // Both outcomes end the same way. On success the returned Blob is the upload source, so the App Group copy is redundant and handing it to the page would make the page responsible for deleting it. On failure, resetting the state alone would leave the file for every later scan to re-detect and fail on again: the 24h sweep skips the scan winner, and an unread share is always the winner.
      this.state.set({ phase: 'idle' });
      // Awaited, not fired and forgotten: this runs inside the serial queue, and letting the next detectAndAdopt start first would let it re-find the file that is about to disappear.
      await this.nativeDeleteFile(item.id);
    }
  }

  // Persists the app-selected language into the App Group so the Share Extension (a separate iOS process) can localize its own alerts — see ShareViewController.swift's resolvedLanguageKey().
  public setSelectedLanguage(languageCode: string): void {
    const methodName = 'setSelectedLanguage';
    if (!this.platformService.isCordova() || !this.platformService.isIOS()) {
      return;
    }
    cordova.plugins.shareExtension.setSelectedLanguage(
      languageCode,
      () => { /* no-op */ },
      (err: any) => this.logger.error(methodName + ' ShareExtension: setSelectedLanguage FAILED — ' + JSON.stringify({ error: String(err) }))
    );
  }

  // ############### PRIVATE ###############

  // Scans native for a pending share and adopts it, superseding whatever was tracked before.
  private async detectAndAdopt(trigger: string): Promise<void> {
    const methodName = 'detectAndAdopt';
    const current = this.state();
    // Skips 'detecting' on a RE-SCAN only (e.g. resume); a first-ever scan still reports pendingShareIntent=false for up to 5s even via handleShareURL — known low-probability gap, OAuth normally outlasts it.
    if (current.phase === 'idle') {
      this.state.set({ phase: 'detecting' });
    }

    const filePath = await this.nativeGetPendingFilePath();
    if (!filePath) {
      // A null here can also mean native error or 5s timeout — only drop state that was speculative.
      if (current.phase === 'idle') {
        this.state.set({ phase: 'idle' });
      }
      return;
    }

    const storageName = filePath.split('/').pop() ?? filePath;
    const item: IShareItem = { id: storageName, displayName: this.displayNameFrom(storageName) };
    const staleId = 'item' in current ? current.item.id : null;

    if (staleId === item.id) {
      // Same id means literally the same share still on disk (a re-share always gets a fresh UUID) — whatever phase it is in, it is already tracked correctly.
      return;
    }

    // Run inside NgZone so DocumentPrintPage's pendingShare effect reliably fires from this native-bridge callback.
    this.ngZone.run(() => {
      this.logger.info(methodName + ': ShareExtension pending file detected ' + JSON.stringify({ displayName: item.displayName, trigger }));
      this.state.set({ phase: 'ready', filePath, item });
      this.navigateToDocumentPrintIfAuthenticated();
    });

    if (staleId) {
      // Only delete the superseded file after confirming via the real scan that it's genuinely a different share.
      await this.nativeDeleteFile(staleId);
    }
  }

  // Promise wrapper around getPendingFilePath; never rejects — native error or 5s bridge hang both resolve null so the queue is never stuck.
  private nativeGetPendingFilePath(): Promise<string | null> {
    const methodName = 'nativeGetPendingFilePath';
    const bridgeCall = new Promise<string | null>((resolve) => {
      cordova.plugins.shareExtension.getPendingFilePath(
        (filePath: string | null) => resolve(filePath),
        (err: any) => {
          this.logger.error(methodName + ' ShareExtension: getPendingFilePath FAILED — ' + JSON.stringify({ error: String(err) }));
          resolve(null);
        }
      );
    });

    let timerId: ReturnType<typeof setTimeout>;
    const timeout = new Promise<null>((resolve) => {
      timerId = setTimeout(() => {
        this.logger.warn(methodName + ' ShareExtension: getPendingFilePath timed out after 5s — treating as no pending file');
        resolve(null);
      }, GET_PENDING_FILE_PATH_TIMEOUT_MS);
    });

    return Promise.race([bridgeCall, timeout]).finally(() => clearTimeout(timerId));
  }

  // Reads a PDF by storage id and returns a JS File (base64 → Blob → File); rejects after 10s if the native bridge doesn't respond.
  private nativeReadFile(item: IShareItem): Promise<File> {
    const methodName = 'nativeReadFile';
    const cordovaCall = new Promise<File>((resolve, reject) => {
      cordova.plugins.shareExtension.readSharedFile(
        item.id,
        (res: { base64: string; mimeType: string }) => {
          try {
            const arrayBuffer = this.base64ToArrayBuffer(res.base64);
            // WKWebView doesn't propagate `name` through the File constructor — attach it to the Blob directly. Must be the display name: it becomes the print job title via DocumentPrintService.initiateUpload.
            const blob  = new Blob([arrayBuffer as unknown as BlobPart], { type: res.mimeType });
            const file  = Object.assign(blob, { name: item.displayName, lastModified: Date.now() }) as unknown as File;
            this.logger.info(methodName + ' ShareExtension: file loaded ' + JSON.stringify({ name: file.name, size: file.size }));
            resolve(file);
          } catch (err) {
            this.logger.error(methodName + ' ShareExtension: FAILED creating File — ' + JSON.stringify({ error: String(err) }));
            reject(new Error('Step 20: ' + String(err)));
          }
        },
        (err: any) => {
          this.logger.error(methodName + ' ShareExtension: readSharedFile FAILED — ' + JSON.stringify({ error: String(err) }));
          reject(new Error('Step 19: ' + String(err)));
        }
      );
    });

    let timerId: ReturnType<typeof setTimeout>;
    const timeout = new Promise<never>((_, reject) => {
      timerId = setTimeout(() => reject(new Error('Step 18: native bridge timeout after 10s')), READ_FILE_TIMEOUT_MS);
    });

    return Promise.race([cordovaCall, timeout]).finally(() => clearTimeout(timerId));
  }

  // Deletes a file from the App Group/Documents; swallows native errors and a 5s bridge hang alike instead of rejecting, so cleanup never blocks the queue.
  private nativeDeleteFile(id: string): Promise<void> {
    const methodName = 'nativeDeleteFile';
    if (!this.platformService.isCordova() || !this.platformService.isIOS()) {
      return Promise.resolve();
    }
    const bridgeCall = new Promise<void>((resolve, reject) => {
      cordova.plugins.shareExtension.deleteSharedFile(id, resolve, reject);
    }).catch(err => {
      this.logger.warn(methodName + ' ShareExtension: deleteSharedFile FAILED (non-fatal) — ' + JSON.stringify({ error: String(err) }));
    });

    let timerId: ReturnType<typeof setTimeout>;
    const timeout = new Promise<void>((resolve) => {
      timerId = setTimeout(() => {
        this.logger.warn(methodName + ' ShareExtension: deleteSharedFile timed out after 5s — treating as done (non-fatal)');
        resolve();
      }, DELETE_FILE_TIMEOUT_MS);
    });

    return Promise.race([bridgeCall, timeout]).finally(() => clearTimeout(timerId));
  }

  private navigateToDocumentPrintIfAuthenticated(): void {
    const methodName = 'navigateToDocumentPrintIfAuthenticated';

    if (!this.authService.isAuthenticatedSignal()) {
      return;
    }

    if (this.router.url.startsWith('/document-print')) {
      return;
    }

    // Cold-start: auth pages read pendingShareIntent() and navigate themselves. Warm-start: navigate directly from here.
    this.logger.info('ShareExtension: navigating to /document-print from ' + this.router.url);
    this.router.navigate(['/document-print']).then(() => {
      this.logger.info('ShareExtension: navigated to /document-print');
    }).catch(err => {
      this.logger.error(methodName, 'ShareExtension: navigate FAILED — ' + JSON.stringify(err));
    });
  }

  // Strips the "<UUID>_" the Share Extension prepends; a name written by an older extension build has no prefix and is its own display name.
  private displayNameFrom(storageName: string): string {
    return STORAGE_PREFIX_RE.test(storageName) ? storageName.slice(STORAGE_PREFIX_LENGTH) : storageName;
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binary = atob(base64);
    const buffer = new ArrayBuffer(binary.length);
    const bytes  = new Uint8Array(buffer);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return buffer;
  }
}
